import { useEffect, useRef, useState } from "react";
import { GridColumnProps } from "@progress/kendo-react-grid";
import {
	ExcelExport,
	ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { PDFExport } from "@progress/kendo-react-pdf";
import { saveAs } from "@progress/kendo-file-saver";
import { FileText, FileSpreadsheet, FileDown, Download } from "lucide-react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "../../components/ui/dialog";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Button } from "../../components/ui/button";
import { showNotification } from "../../store/Central/selectors";
import { Logger } from "@/lib/logger/Logger";
import { ExtendedArticleEntityType } from "../../store/Jobs/types";
import {
	ArticleExportFormat,
	articleExportFilename,
	buildArticleCsv,
	buildArticleExportRows,
} from "./article-export.utils";
// Embedded Liberation Sans — the same font as the Debitoren export PDFs — so
// all exported documents share one look and umlauts render correctly.
import "../../pdf-templates/fonts.css";

const PDF_FONT_FAMILY = '"LiberationSans", Arial, sans-serif';

const FORMAT_META: Record<
	ArticleExportFormat,
	{ label: string; description: string; Icon: typeof FileText }
> = {
	xlsx: {
		label: "Excel",
		description: "Excel (Microsoft Excel Format)",
		Icon: FileSpreadsheet,
	},
	pdf: {
		label: "PDF",
		description: "PDF (druckfähiges Dokument)",
		Icon: FileDown,
	},
	csv: {
		label: "CSV",
		description: "CSV (Komma-getrennte Werte)",
		Icon: FileText,
	},
};

const columnWidthPx = (column: GridColumnProps): number =>
	parseInt(String(column.width), 10) || 120;

type PdfOrientation = "portrait" | "landscape";

// Printable width of an A4 page in the units Kendo's PDF renderer uses (1 CSS px
// ≈ 1 PDF point / 72 DPI): A4 is 595.3pt × 841.9pt, minus the 0.6cm (~17pt) of
// horizontal margin set below, with a couple of points of safety so the right
// column never clips. The PDF table width is set to exactly this so every
// selected column fits on the page.
const PDF_PAGE_WIDTH_PX: Record<PdfOrientation, number> = {
	portrait: 575,
	landscape: 820,
};

// The PDF table is laid out at 100% page width with auto column sizing, so every
// selected column always fits (text wraps instead of clipping). To keep it dense
// and readable, the font shrinks as columns get tighter — more columns means less
// width each, down to a legible floor.
const pdfFontSize = (
	columnCount: number,
	orientation: PdfOrientation
): number => {
	const perColumn = PDF_PAGE_WIDTH_PX[orientation] / Math.max(columnCount, 1);
	if (perColumn >= 90) return 10;
	if (perColumn >= 68) return 9.5;
	if (perColumn >= 52) return 9;
	if (perColumn >= 42) return 8;
	if (perColumn >= 34) return 7.5;
	return 7;
};

const formatExportDate = (date: Date): string =>
	`${date.toLocaleDateString("de-CH")}, ${date.toLocaleTimeString("de-CH", {
		hour: "2-digit",
		minute: "2-digit",
	})} Uhr`;

interface ArticleExportInfo {
	searchTerm: string;
	columnFilters: Record<string, string>;
	sort: Array<{ field: string; dir?: "asc" | "desc" }>;
	totalCount: number;
}

interface ArticleExportDialogProps {
	columns: GridColumnProps[];
	getArticles: () => ExtendedArticleEntityType[];
	exportInfo: ArticleExportInfo;
}

export const ArticleExportDialog: React.FC<ArticleExportDialogProps> = ({
	columns,
	getArticles,
	exportInfo,
}) => {
	const [open, setOpen] = useState(false);
	const [format, setFormat] = useState<ArticleExportFormat>("xlsx");
	const [orientation, setOrientation] = useState<PdfOrientation>("landscape");
	const [selectedFields, setSelectedFields] = useState<Set<string>>(
		new Set()
	);

	const excelExportRef = useRef<ExcelExport>(null);
	const pdfExportRef = useRef<PDFExport>(null);
	const [pdfExportData, setPdfExportData] = useState<{
		rows: Record<string, string | number>[];
		exportedAt: Date;
	} | null>(null);

	// The PDF is drawn from the off-screen HTML table below, so we render the
	// rows first and trigger the save once they're in the DOM.
	useEffect(() => {
		if (!pdfExportData) return;
		const id = requestAnimationFrame(() => {
			pdfExportRef.current?.save();
			setPdfExportData(null);
		});
		return () => cancelAnimationFrame(id);
	}, [pdfExportData]);

	// Default the column selection to whatever is currently visible in the grid.
	useEffect(() => {
		if (!open) return;
		setSelectedFields(
			new Set(
				columns
					.filter((column) => (column as any).show !== false)
					.map((column) => column.field as string)
			)
		);
	}, [open]);

	const selectedColumns = columns.filter((column) =>
		selectedFields.has(column.field as string)
	);

	const pdfFont = pdfFontSize(selectedColumns.length, orientation);

	const toggleField = (field: string, checked: boolean) => {
		setSelectedFields((prev) => {
			const next = new Set(prev);
			if (checked) next.add(field);
			else next.delete(field);
			return next;
		});
	};

	const setAll = (checked: boolean) => {
		setSelectedFields(
			checked
				? new Set(columns.map((column) => column.field as string))
				: new Set()
		);
	};

	const handleExport = () => {
		if (selectedColumns.length === 0) {
			showNotification({
				message: "Bitte mindestens eine Spalte auswählen",
				type: "error",
			});
			return;
		}

		try {
			const fields = selectedColumns.map(
				(column) => column.field as string
			);
			const rows = buildArticleExportRows(getArticles(), fields);

			if (format === "csv") {
				const csv = buildArticleCsv(
					rows,
					selectedColumns.map((column) => ({
						field: column.field as string,
						title: column.title ?? (column.field as string),
					}))
				);
				saveAs(
					new Blob([csv], { type: "text/csv;charset=utf-8" }),
					articleExportFilename("csv", new Date())
				);
			} else if (format === "xlsx") {
				excelExportRef.current?.save(rows);
			} else {
				setPdfExportData({ rows, exportedAt: new Date() });
			}
			setOpen(false);
		} catch (error) {
			Logger.error(error, {}, "Article export failed");
			showNotification({
				message: "Fehler beim Exportieren der Artikel",
				type: "error",
			});
		}
	};

	const { Icon: SelectedIcon, description } = FORMAT_META[format];
	const allSelected = selectedFields.size === columns.length;

	const columnTitle = (field: string) =>
		columns.find((column) => column.field === field)?.title ?? field;

	const filterParts = [
		...(exportInfo.searchTerm ? [`Suche „${exportInfo.searchTerm}“`] : []),
		...Object.entries(exportInfo.columnFilters).map(
			([field, value]) => `${columnTitle(field)} enthält „${value}“`
		),
	];
	const sortLabel = exportInfo.sort.length
		? exportInfo.sort
				.map(
					(entry) =>
						`${columnTitle(entry.field)} (${
							entry.dir === "desc" ? "absteigend" : "aufsteigend"
						})`
				)
				.join(", ")
		: "Standard";

	const pdfTotalColumnWidth = selectedColumns.reduce(
		(sum, column) => sum + columnWidthPx(column),
		0
	);

	return (
		<>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						icon={<Download />}
						shortcut="e"
						aria-label="Artikel exportieren"
						data-testid="articles-table-export-button"
					>
						Export
					</Button>
				</DialogTrigger>
				<DialogContent className="sm:max-w-[480px]">
					<DialogHeader>
						<DialogTitle>Artikel exportieren</DialogTitle>
						<DialogDescription>
							Wählen Sie das Format und die zu exportierenden
							Spalten. Es werden alle gefilterten Artikel in der
							aktuellen Sortierung exportiert.
						</DialogDescription>
					</DialogHeader>

					<div className="grid gap-4 py-2">
						<div className="grid gap-2">
							<Label htmlFor="export-format">Format</Label>
							<Select
								value={format}
								onValueChange={(value: ArticleExportFormat) =>
									setFormat(value)
								}
							>
								<SelectTrigger id="export-format">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{(
										Object.keys(
											FORMAT_META
										) as ArticleExportFormat[]
									).map((key) => {
										const { label, Icon } =
											FORMAT_META[key];
										return (
											<SelectItem key={key} value={key}>
												<div className="flex items-center gap-2">
													<Icon className="h-4 w-4" />
													<span>{label}</span>
												</div>
											</SelectItem>
										);
									})}
								</SelectContent>
							</Select>
							<p className="text-xs text-gray-500">
								{description}
							</p>
						</div>

						{format === "pdf" && (
							<div className="grid gap-2">
								<Label htmlFor="export-orientation">
									Ausrichtung
								</Label>
								<Select
									value={orientation}
									onValueChange={(value: PdfOrientation) =>
										setOrientation(value)
									}
								>
									<SelectTrigger id="export-orientation">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="portrait">
											Hochformat
										</SelectItem>
										<SelectItem value="landscape">
											Querformat
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						)}

						<div className="grid gap-2">
							<div className="flex items-center justify-between">
								<Label>Spalten</Label>
								<button
									type="button"
									className="text-xs text-primary hover:underline"
									onClick={() => setAll(!allSelected)}
								>
									{allSelected
										? "Alle abwählen"
										: "Alle auswählen"}
								</button>
							</div>
							<div className="max-h-60 overflow-y-auto rounded-md border p-2 grid grid-cols-2 gap-1.5">
								{columns.map((column) => {
									const field = column.field as string;
									const checked = selectedFields.has(field);
									return (
										<label
											key={field}
											className="flex items-center gap-2 text-sm cursor-pointer py-0.5"
										>
											<Checkbox
												checked={checked}
												onCheckedChange={(value) =>
													toggleField(
														field,
														value === true
													)
												}
											/>
											<span className="truncate">
												{column.title}
											</span>
										</label>
									);
								})}
							</div>
							<p className="text-xs text-gray-500">
								{selectedFields.size} von {columns.length}{" "}
								Spalten ausgewählt
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setOpen(false)}
						>
							Abbrechen
						</Button>
						<Button
							onClick={handleExport}
							className="flex items-center gap-2"
						>
							<SelectedIcon className="h-4 w-4" />
							Als {FORMAT_META[format].label} exportieren
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Off-screen Kendo exporters; columns mirror the current selection. */}
			<div
				style={{ position: "fixed", left: -10000, top: 0 }}
				aria-hidden
			>
				<ExcelExport
					ref={excelExportRef}
					fileName={articleExportFilename("xlsx", new Date())}
				>
					{selectedColumns.map((column) => (
						<ExcelExportColumn
							key={column.field}
							field={column.field as string}
							title={column.title}
							width={columnWidthPx(column)}
						/>
					))}
				</ExcelExport>
				<PDFExport
					ref={pdfExportRef}
					fileName={articleExportFilename("pdf", new Date())}
					paperSize="A4"
					landscape={orientation === "landscape"}
					margin={{
						top: "1.1cm",
						bottom: "0.5cm",
						left: "0.3cm",
						right: "0.3cm",
					}}
					repeatHeaders
					pageTemplate={(props: {
						pageNum?: number;
						totalPages?: number;
					}) => (
						// One header row on every page: title, export
						// metadata and page number — no footer. Must be
						// anchored with an explicit top, otherwise Kendo
						// places the template after the page content.
						<div
							style={{
								position: "absolute",
								top: 8,
								left: 12,
								right: 12,
								display: "flex",
								alignItems: "baseline",
								gap: 8,
								borderBottom: "1px solid #9ca3af",
								paddingBottom: 4,
								color: "#000",
								fontFamily: PDF_FONT_FAMILY,
							}}
						>
							<span
								style={{
									fontSize: 13,
									fontWeight: 700,
									whiteSpace: "nowrap",
								}}
							>
								Artikelliste
							</span>
							<span style={{ fontSize: 8.5, color: "#000" }}>
								Exportiert am{" "}
								{pdfExportData
									? formatExportDate(pdfExportData.exportedAt)
									: ""}{" "}
								· {pdfExportData?.rows.length} von{" "}
								{exportInfo.totalCount} Artikeln · Sortierung:{" "}
								{sortLabel} ·{" "}
								{filterParts.length
									? `Filter: ${filterParts.join(" · ")}`
									: "Keine Filter aktiv"}
							</span>
							<span
								style={{
									fontSize: 8.5,
									color: "#000",
									marginLeft: "auto",
									whiteSpace: "nowrap",
								}}
							>
								Seite {props.pageNum} / {props.totalPages}
							</span>
						</div>
					)}
				>
					{pdfExportData && (
						<div
							style={{
								width: PDF_PAGE_WIDTH_PX[orientation],
								fontFamily: PDF_FONT_FAMILY,
							}}
						>
							<table
								className="articles-pdf-table"
								style={{
									width: PDF_PAGE_WIDTH_PX[orientation],
									fontSize: pdfFont,
								}}
							>
								<colgroup>
									{selectedColumns.map((column) => (
										<col
											key={column.field}
											style={{
												width: `${
													(columnWidthPx(column) /
														pdfTotalColumnWidth) *
													100
												}%`,
											}}
										/>
									))}
								</colgroup>
								<thead>
									<tr>
										{selectedColumns.map((column) => (
											<th key={column.field}>
												{column.title}
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{pdfExportData.rows.map((row, index) => (
										<tr key={index}>
											{selectedColumns.map((column) => (
												<td key={column.field}>
													{
														row[
															column.field as string
														]
													}
												</td>
											))}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</PDFExport>
			</div>
		</>
	);
};
